import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import { Dispatcher as UsersGroupsDispatcher } from '../../../../_common/platform/ts/usersGroupsManagement/Dispatcher';
import { Actions as UsersGroupsActions } from '../../../../_common/platform/ts/usersGroupsManagement/Actions';
import { Store as UsersGroupsStore } from '../../../../_common/platform/ts/usersGroupsManagement/Store';
import { ngettext } from '../../../../_common/platform/ts/ngettext';
import TreeSelect from '../../../../_common/platform/tsx/TreeSelect';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import { IDisplayValue } from '../../../../_common/platform/ts/Interfaces';
import { IDataParameters } from '../../../../_common/platform/ts/AqlUtility';
import { nullHandlingBackend } from '../../../../_common/platform/ts/nullHandling';

import { IEntityValuesTree } from '../../ts/stores/EntityModelsStore';
import { ITreeNode, ITreeNodesCollection } from '../../ts/Interfaces';

interface IUserGroups {
    [id: number]: number[];
}

interface IUserGroupsExportProps {
    value: IUserGroups;
    theme: IValuesCollection;
    glob: IGlob;
    onChange(val: IUserGroups, selectedItems: any[]): void;
}

export const UserGroupsExport: React.FC<IUserGroupsExportProps> = (props) => {
    const [value, setValue] = React.useState<IUserGroups>({});

    React.useEffect(() => {
        if (props.glob.UsersGroupsDispatcher == null) {
            props.glob.UsersGroupsDispatcher = UsersGroupsDispatcher.get();
        }

        if (props.glob.UsersGroupsActions == null) {
            props.glob.UsersGroupsActions = UsersGroupsActions.get(props.glob.UsersGroupsDispatcher);
        }

        if (props.glob.UsersGroupsStore == null) {
            props.glob.UsersGroupsStore = UsersGroupsStore.get(
                props.glob,
                props.glob.UsersGroupsDispatcher,
                props.glob.UsersGroupsActions
            );
        }

        setValue(props.value || {});
    }, []);

    const getSelectedMessage = (value: IUserGroups) => {
        const groups = Object.keys(value).map((key) => parseInt(key));
        const usersCount = groups.reduce((acc, groupId) => acc + value[groupId].length, 0);

        if (groups.length === 0 && usersCount === 0) {
            return [<span key={'ask-ws-span'}>Select groups and users to export.</span>];
        }

        const arr = _.compact([
            usersCount > 0 ? (
                <div key={'template-div-ws'} className="selected-message-block">
                    <strong>{usersCount}</strong>
                    <span>{ngettext('user', 'users', usersCount)}</span>
                </div>
            ) : null,

            groups.length > 0 ? (
                <div key={'dirs-div-ws'} className="selected-message-block">
                    <strong>{groups.length}</strong>
                    <span>{ngettext('group', 'groups', groups.length)}</span>
                </div>
            ) : null
        ]);

        if (arr.length === 2) {
            arr.splice(1, 0, <span key={'and-span-ws'}>{'in'}</span>);
        }

        arr.unshift(<span key={'you-span-ws'}>{'You have selected'}</span>);

        return arr;
    };

    const getInputGroupsFromDb = (def) => {
        const keyAttributeUsers = props.glob.EntityModelsStore.getKeyAttribute('wwUsers', dataSourceID);
        const keyAttributeGroups = props.glob.EntityModelsStore.getKeyAttribute('wwUserGroups', dataSourceID);
        const params: IDataParameters = {
            dataSourceID: dataSourceID,
            attrib_refs: [
                { attrib: ['wwUserGroups', keyAttributeGroups || 'id'] },
                { attrib: ['wwUserGroups'], sort: 1, sort_desc: false },
                { attrib: ['wwUserGroups', '^wwUserGroupMembers', '^wwUsers', keyAttributeUsers || 'userId'] },
                { attrib: ['wwUserGroups', '^wwUserGroupMembers', '^wwUsers'] }
            ],
            group_attribs: [],
            filter: [],
            queryRootEntity: 'wwUserGroups',
            nullHandling: nullHandlingBackend.OMIT_NULLS,
            distinct: true,
            maxNumberOfRecords: 0,
            maxCacheAge: 0,
            minCacheTime: 0
        };

        props.glob.AQLQueries.loadData(params, {}).done((resp) => {
            const tree: ITreeNode[] = [];
            const values: ITreeNodesCollection = {};

            const groupedById = resp.result.reduce((groups, item) => {
                const groupKey = item[0];

                if (!groups[groupKey]) {
                    groups[groupKey] = [];
                }
                
                groups[groupKey].push(item);
                return groups;
            }, {});

            Object.keys(groupedById).forEach((groupKey) => {
                const groupId = 'group-' + groupKey;
                const treeNode: ITreeNode = {
                    key: groupId,
                    id: groupId,
                    title: groupedById[groupKey][0][1],
                    subNodes: groupedById[groupKey].map<ITreeNode>((item) => {
                        const id = 'user-' + groupKey + '-' + item[2];

                        return {
                            key: id,
                            id: id,
                            title: item[3],
                            parentId: 'group-' + groupKey,
                            subNodes: []
                        };
                    })
                };

                const value: ITreeNode = {
                    key: groupId,
                    id: groupId,
                    title: groupedById[groupKey][0][1],
                    subNodes: groupedById[groupKey].map<ITreeNode>((item) => {
                        const userId = 'user-' + groupKey + '-' + item[2];
                        const userValue: ITreeNode = {
                            key: userId,
                            id: userId,
                            title: item[3],
                            parentId: 'group-' + groupKey,
                            subNodes: []
                        };
                        values[userId] = userValue;
                    })
                };

                tree.push(treeNode);
                values[groupId] = value;
            });

            const result: IEntityValuesTree = {
                tree: tree.sort((a, b) => a.title.localeCompare(b.title)),
                nodes: values
            };

            def.resolve(result);
        });
    };

    const getInputUserGroupTree = () => {
        return () => {
            const def = $.Deferred<IEntityValuesTree>();

            getInputGroupsFromDb(def);
            return def;
        };
    };

    const handleOnValueChanged = (values: IDisplayValue[]) => {
        const newValue: IUserGroups = {};

        values.forEach((val) => {
            if (val.value.startsWith('group-')) {
                const groupId = parseInt(val.value.replace('group-', ''));

                if (newValue[groupId] == null) {
                    newValue[groupId] = [];
                }
            }

            if (val.value.startsWith('user-')) {
                const match = val.value.match(/user-(\d+)-(\d+)/);

                if (match) {
                    const groupId = parseInt(match[1], 10);
                    const userId = parseInt(match[2], 10);

                    if (newValue[groupId] != null) {
                        let members = newValue[groupId];

                        members.push(userId);
                        newValue[groupId] = [...new Set(members)]; // unique elements only
                    } else {
                        newValue[groupId] = [userId];
                    }
                }
            }
        });

        setValue(newValue);

        if (typeof props.onChange === 'function') {
            props.onChange(newValue, values);
        }
    };

    const dataSourceID = props.glob.EntityModelsStore.getDataSourceIDByName('Default');

    const convertValue = (_value: IUserGroups) => {
        const output: IDisplayValue[] = [];

        const groups = Object.keys(_value).map((key) => parseInt(key));

        groups.forEach((group) => {
            output.push({ value: 'group-' + group.toString() });
            _value[group].forEach((user) => {
                output.push({ value: 'user-' + group.toString() + '-' + user.toString() });
            });
        });

        return output;
    };

    return (
        <div className="usergroup-export">
            <div className="selected-message">{getSelectedMessage(value)}</div>
            <TreeSelect
                glob={props.glob}
                theme={props.theme}
                value={convertValue(value)}
                allowMultiselection={true}
                attributeData={{
                    attribute: 'wwUserGroupsTree'
                }}
                autoSelectChildNodes={false}
                dataSourceID={dataSourceID}
                getValues={getInputUserGroupTree()}
                onChange={handleOnValueChanged}
            />
        </div>
    );
};
